/* eslint-disable jsx-a11y/anchor-has-content */
// Report layout V2 - report for questionnaire type 2.
//
// Data loading, saving and the AI calls live in ReportBase; this file is the
// layout only. The building blocks live in ./v2:
//   theme.js          colour tokens + placeholder image paths
//   V2Page.js         page shell (light / dark / watermark variants)
//   V2Donut.js        concentric donut for the overall result
//   V2BarChart.js     grouped bar charts
//   V2BenchmarkBars.js  dual-scale benchmark rows
//   V2Elements.js     hexagons, cards, timeline, chips
import React from "react";
import { Helmet } from "react-helmet";
import { NavLink } from "react-router-dom";

import {
  Bar,
  BarChart,
  ComposedChart,
  LabelList,
  Legend,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";

import ReportBase from "./ReportBase";
import ReportCategoryHightlightsSuggestions from "../../components/report/ReportCategoryHightlightsSuggestions";
import ReportChart from "../../components/report/ReportChart";
import ReportOveral from "../../components/report/ReportOveral";
import { Loader } from "../../components";
import { getCountries, getLanguage } from "../../utils/utils";

import V2Page, { V2Header } from "./v2/V2Page";
import V2Donut from "./v2/V2Donut";
import V2BarChart from "./v2/V2BarChart";
import V2BenchmarkBars from "./v2/V2BenchmarkBars";
import V2EditableCards from "./v2/V2EditableCards";
import V2EditableHighlights, { isEmptyHtml } from "./v2/V2EditableHighlights";
import V2EditableSentence from "./v2/V2EditableSentence";
import V2EditableTimeline from "./v2/V2EditableTimeline";
import {
  V2Card,
  V2Chip,
  V2HexCard,
  V2HexStat,
  V2Lockup,
} from "./v2/V2Elements";
import {
  colors,
  elcDiagrams,
  images,
  GRADE_THRESHOLD,
  RESULT_THRESHOLD,
} from "./v2/theme";
import v2Copy, { contact } from "./v2/translations";

import "../css/report.css";
import "../css/report-v2.css";

// Placement of the four hexagons on the SELECTIO services slide, in the order
// they appear in v2Copy.services: Hiring, Assessment, HR Consulting, Educations.
//
// They interlock at offsets that do not fall on a regular grid, so rather than
// deriving the positions each one is given explicit coordinates inside the
// cluster box (.report-v2__hex-cards, which is position: relative). To move a
// hexagon, change its left/top here - nothing else depends on these values.
const servicesHexPositions = [
  { left: 0, top: 18 }, // Hiring
  { left: 190, top: 18 }, // Assessment
  { left: 95, top: 182 }, // HR Consulting
  { left: 285, top: 182 }, // Educations
];

export default class ReportV2 extends ReportBase {
  // Editable card lists are stored as JSON under a single report content key.
  // Falls back to the translated default the first time a report is opened, so
  // an untouched report still shows the standard steps.
  _cardList = (name, fallback) => {
    const stored = this.state.content ? this.state.content[name] : null;

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch (error) {
        // Fall through to the default below rather than losing the slide.
      }
    }

    return fallback || [];
  };

  // The benchmark selector shown beside the benchmark slides. Rendered outside
  // the slide itself by ReportChart, so it never appears in the printed report.
  _renderBenchmarkOptions = () => {
    const { categories, data, messages, benchmarkLoading } = this.state;

    return (
      <div className="report__options">
        <div className="report__benchmark">
          <h3>{messages.benchmarkTitle}</h3>
          <form method="post" onSubmit={this._benchmarkOnSubmit}>
            <fieldset className="fieldset">
              <label className="label">{messages.country}</label>
              <select
                name="benchmarkCountry"
                className="input"
                onChange={this._benchmarkOnChange}
              >
                <option value="">-</option>
                {getCountries().map((item, key) => (
                  <option key={key} value={item}>
                    {messages.countries[item]}
                  </option>
                ))}
              </select>
            </fieldset>
            <fieldset className="fieldset">
              <label className="label">{messages.industry}</label>
              <select
                name="benchmarkIndustry"
                className="input input--medium"
                onChange={this._benchmarkOnChange}
              >
                <option value="">-</option>
                {(categories || []).map((item, key) => (
                  <option key={key} value={item.id}>
                    {messages.primaryBusinessType[item.id]}
                  </option>
                ))}
              </select>
            </fieldset>
            {data.info.company.group && (
              <fieldset className="fieldset">
                <label className="label">{messages.companyGroup}</label>
                <select
                  name="benchmarkGroup"
                  className="input input--medium"
                  onChange={this._benchmarkOnChange}
                >
                  <option value="">-</option>
                  <option value="1">{messages.show}</option>
                </select>
              </fieldset>
            )}
            <fieldset>
              {benchmarkLoading ? (
                <Loader />
              ) : (
                <button className="button">{messages.benchmarkSubmit}</button>
              )}
            </fieldset>
          </form>
        </div>
      </div>
    );
  };

  // The report payload keeps the current and previous grade per key topic /
  // category; the charts want one row per label with a key per series.
  _seriesData = (items) => {
    return (items || []).map((item) => ({
      name: item.content,
      current: item.grade,
      previous: item.previousGrade,
      benchmark: item.others,
      benchmarkCountry: item.benchmarkCountry,
      benchmarkIndustry: item.benchmarkIndustry,
      benchmarkGroup: item.benchmarkGroup,
    }));
  };

  renderReport() {
    const {
      id,
      data,
      editable,
      messages,
      content,
      aiItems,
      aiLoading,
      benchmarkCountry,
      benchmarkIndustry,
      benchmarkGroup,
      benchmarkLoading,
    } = this.state;

    if (!data) {
      return <div className="loading">{messages.loading}</div>;
    }

    const year = data.info.year;
    const previousYear = data.info.previousYear;
    const hasPrevious = previousYear !== null && previousYear !== undefined;

    const benchmarkSeries = [
      {
        key: "current",
        name: messages.yourOrganization,
        color: colors.primary,
      },
      {
        key: "benchmark",
        name: messages.employerPartnerAvg,
        color: colors.secondary,
      },
    ];

    // The per-step charts show this year, last year and the Employer Partner
    // average by default. A selected country / industry / group benchmark is
    // added only once it has actually been chosen.
    const categorySeries = [
      { key: "current", name: String(year), color: colors.primary },
      ...(hasPrevious
        ? [
            {
              key: "previous",
              name: String(previousYear),
              color: colors.tertiary,
            },
          ]
        : []),
      {
        key: "benchmark",
        name: messages.employerPartnerAvg,
        color: colors.secondary,
      },
      ...(benchmarkCountry
        ? [
            {
              key: "benchmarkCountry",
              name: messages.countries[benchmarkCountry],
              color: colors.threshold,
            },
          ]
        : []),
      ...(benchmarkIndustry
        ? [
            {
              key: "benchmarkIndustry",
              name: messages.primaryBusinessType[benchmarkIndustry],
              color: colors.threshold,
            },
          ]
        : []),
      ...(benchmarkGroup
        ? [
            {
              key: "benchmarkGroup",
              name: messages.companyGroup,
              color: colors.threshold,
            },
          ]
        : []),
    ];

    // Page numbers are handed out in render order rather than hardcoded - the
    // number of pages depends on how many Employee Life Cycle categories the
    // questionnaire has, and this way inserting or reordering a page keeps the
    // rest correct. The cover and the closing slide carry no number, so the
    // first numbered page is 2, matching the printed report.
    let pageNumber = 1;
    const nextPage = () => (pageNumber += 1);

    return (
      <div className="page__wrapper">
        {!editable && (
          <div className="page__notice">{messages.reportLocked}</div>
        )}
        <div className="page__report report__v2">
          <h2>{messages.report}</h2>
          {/* Also the default filename when the report is printed to PDF. */}
          <Helmet>
            <title>{`${v2Copy.pageTitle} - ${data.info.name} ${year}`}</title>
          </Helmet>
          <div className="backlink">
            <NavLink to={`/${id}`} />
          </div>
          <div className="report__print">
            <button className="button" onClick={() => window.print()}>
              {messages.download}
            </button>
          </div>

          {/* Cover */}
          <V2Page variant="dark" modifier="cover" background={images.cover}>
            <V2Lockup
              selectio={images.logoSelectioWhite}
              employerPartner={images.logoEpWhite}
            />
            <h1 className="report-v2__cover-title">{v2Copy.coverTitle}</h1>
            <p className="report-v2__cover-for">{v2Copy.coverResultsFor}</p>
            <p className="report-v2__cover-company">{data.info.name}</p>
            <div className="report-v2__chips">
              <V2Chip>
                {v2Copy.statusLabel}:{" "}
                {data.info.passed
                  ? v2Copy.statusCertified
                  : v2Copy.statusNotCertified}
              </V2Chip>
              <V2Chip>
                {v2Copy.yearLabel}: {year}
              </V2Chip>
            </div>
          </V2Page>

          {/* Index - anchors link to the pages further down. The per-category
              entries are generated so the list matches the actual report. */}
          <V2Page number={nextPage()} modifier="index">
            <V2Header title={v2Copy.index} />
            <ul className="report-v2__index">
              {v2Copy.indexItems.map((item, key) => (
                <li key={key}>
                  <a href={`#${item.id}`}>{item.label}</a>
                </li>
              ))}
            </ul>
          </V2Page>

          {/* Two perspectives - the two diagrams carry the design, so the slide
              is plain white with a centred title. */}
          <V2Page variant="plain" modifier="perspectives" number={nextPage()}>
            <V2Header title={v2Copy.perspectives} />
            <div className="report-v2__split">
              <div style={{ textAlign: "center" }}>
                <img
                  className="report-v2__elc"
                  src={elcDiagrams(getLanguage()).employee}
                  alt={v2Copy.employeePerspective}
                />
                <p>
                  <em>{v2Copy.employeePerspective}</em>
                </p>
              </div>
              <div style={{ textAlign: "center" }}>
                <img
                  className="report-v2__elc"
                  src={elcDiagrams(getLanguage()).business}
                  alt={v2Copy.businessPerspective}
                />
                <p>
                  <em>{v2Copy.businessPerspective}</em>
                </p>
              </div>
            </div>
          </V2Page>

          {/* What's in it for you */}
          <V2Page number={nextPage()} id="page-about" modifier="about">
            <div className="report-v2__split">
              <div className="report-v2__body">
                <V2Header
                  eyebrow={v2Copy.aboutEyebrow}
                  title={v2Copy.whatsInIt}
                />
                <p>{v2Copy.whatsInItIntro}</p>
                <ul className="report-v2__list">
                  {v2Copy.whatsInItList.map((item, key) => (
                    <li key={key}>{item}</li>
                  ))}
                </ul>
                <p>{v2Copy.whatsInItOutro}</p>
              </div>
              <div className="report-v2__hex-stats">
                {v2Copy.stats.map((stat, key) => (
                  <V2HexStat
                    key={key}
                    value={stat.value}
                    label={stat.label}
                    outline={images.hexBlue}
                  />
                ))}
              </div>
            </div>
          </V2Page>

          {/* Scoring methodology */}
          <V2Page modifier="scoring" number={nextPage()}>
            <div className="report-v2__body">
              <V2Header
                eyebrow={v2Copy.aboutEyebrow}
                title={v2Copy.scoringTitle}
              />
              <p>
                {v2Copy.scoringIntro.replace(
                  "{threshold}",
                  String(RESULT_THRESHOLD),
                )}
              </p>
              <div className="report-v2__columns">
                <ul>
                  {(data.keyTopics || []).map((item, key) => (
                    <li key={key}>{item.content}</li>
                  ))}
                </ul>
              </div>
              {/* The notice heading is set like the eyebrow above the title, so
                  it reuses that class rather than a plain heading. */}
              <div className="report-v2__notice">
                <p className="report-v2__eyebrow">
                  {v2Copy.confidentialityTitle}
                </p>
                <p>{v2Copy.confidentialityText}</p>
              </div>
            </div>
          </V2Page>

          {/* Divider: your results */}
          <V2Page variant="dark" modifier="divider" number={nextPage()}>
            <V2Lockup
              selectio={images.logoSelectioWhite}
              employerPartner={images.logoEpWhite}
            />
            <h2 className="report-v2__title">{v2Copy.yourResults}</h2>
            <p>{v2Copy.yourResultsIntro}</p>
          </V2Page>

          {/* Overall result - built on V1's ReportOveral, which already has the
              50/50 split with the text vertically centred against the chart.
              Only the chart itself is the V2 one. */}
          <ReportOveral
            id="page-overall"
            eyebrow={v2Copy.yourResults}
            title={messages.overallResult}
            description={messages.overallResultIntro}
            number={nextPage()}
          >
            <V2Donut
              value={data.result.overal}
              previousValue={data.result.previous}
              threshold={RESULT_THRESHOLD}
              size={260}
              stroke={40}
              legend
              currentLabel={String(year)}
              previousLabel={String(previousYear)}
            />
          </ReportOveral>

          {/* Overall result benchmark - V1's ReportChart layout, which also
              renders the benchmark selector beside the slide. */}
          <ReportChart
            id="page-overall-benchmark"
            eyebrow={v2Copy.yourResults}
            title={messages.overallResult}
            subtitle={messages.benchmarkAnalysis}
            className="report_chart--overall"
            number={nextPage()}
            options={this._renderBenchmarkOptions()}
          >
            {benchmarkLoading ? (
              <Loader />
            ) : (
              <ComposedChart
                width={500}
                height={300}
                data={[
                  {
                    name: "",
                    grade: data.result.overal,
                    others: data.result.average,
                    benchmarkCountry: data.result.benchmarkCountry,
                    benchmarkIndustry: data.result.benchmarkIndustry,
                    benchmarkGroup: data.result.benchmarkGroup,
                  },
                ]}
              >
                <Bar
                  name={messages.yourOrganization}
                  dataKey="grade"
                  fill={colors.primary}
                >
                  <LabelList
                    position="inside"
                    formatter={(value) => `${value}%`}
                  />
                </Bar>
                <Bar
                  name={messages.employerPartnerAvg}
                  dataKey="others"
                  fill={colors.secondary}
                >
                  <LabelList
                    position="inside"
                    style={{ fill: "#fff" }}
                    formatter={(value) => `${value}%`}
                  />
                </Bar>

                {benchmarkCountry && (
                  <Bar
                    name={messages.countries[benchmarkCountry]}
                    dataKey="benchmarkCountry"
                    fill={colors.tertiary}
                  >
                    <LabelList
                      position="inside"
                      style={{ fill: "#fff" }}
                      formatter={(value) => `${value}%`}
                    />
                  </Bar>
                )}

                {benchmarkIndustry && (
                  <Bar
                    name={messages.primaryBusinessType[benchmarkIndustry]}
                    dataKey="benchmarkIndustry"
                    fill={colors.tertiary}
                  >
                    <LabelList
                      position="inside"
                      style={{ fill: "#fff" }}
                      formatter={(value) => `${value}%`}
                    />
                  </Bar>
                )}

                {benchmarkGroup && (
                  <Bar
                    name={messages.companyGroup}
                    dataKey="benchmarkGroup"
                    fill={colors.tertiary}
                  >
                    <LabelList
                      position="inside"
                      style={{ fill: "#fff" }}
                      formatter={(value) => `${value}%`}
                    />
                  </Bar>
                )}

                <XAxis type="category" dataKey="name" width={200} />
                <YAxis type="number" domain={[0, 100]} />
                <Legend
                  align="left"
                  wrapperStyle={{ left: 64 }}
                  formatter={(value) => (
                    <span className="report__legend__label">{value}</span>
                  )}
                />
                <ReferenceLine y={RESULT_THRESHOLD} stroke={colors.threshold} />
              </ComposedChart>
            )}
          </ReportChart>

          {/* Key topics */}
          <ReportChart
            id="page-key-topics"
            eyebrow={v2Copy.yourResults}
            title={messages.resultsKeyTopics}
            description={messages.resultsKeyTopicsDescription}
            number={nextPage()}
          >
            <ComposedChart
              width={500}
              height={350}
              data={data.keyTopics}
              layout="vertical"
            >
              <XAxis
                type="number"
                domain={[0, 3]}
                ticks={[0, 0.5, 1, 1.5, 2, 2.5, 3]}
              />
              <Bar name={year} dataKey="grade" fill={colors.primary} />
              <YAxis type="category" dataKey="content" width={200} />
              <ReferenceLine x={GRADE_THRESHOLD} stroke={colors.threshold} />
              {hasPrevious && (
                <>
                  <Bar
                    name={previousYear}
                    dataKey="previousGrade"
                    fill={colors.secondary}
                  />
                  <Legend
                    align="left"
                    wrapperStyle={{ left: 204 }}
                    formatter={(value) => (
                      <span className="report__legend__label">{value}</span>
                    )}
                  />
                </>
              )}
            </ComposedChart>
          </ReportChart>

          {/* Key topics benchmark */}
          <ReportChart
            id="page-key-topics-benchmark"
            eyebrow={v2Copy.yourResults}
            title={messages.resultsKeyTopics}
            subtitle={messages.benchmarkAnalysis}
            number={nextPage()}
            options={this._renderBenchmarkOptions()}
          >
            <ComposedChart
              width={500}
              height={350}
              data={data.keyTopics}
              layout="vertical"
            >
              <Bar
                name={messages.yourOrganization}
                dataKey="grade"
                fill={colors.primary}
              />
              <Bar
                name={messages.employerPartnerAvg}
                dataKey="others"
                fill={colors.secondary}
              />

              {benchmarkCountry && (
                <Bar
                  name={messages.countries[benchmarkCountry]}
                  dataKey="benchmarkCountry"
                  fill={colors.tertiary}
                />
              )}

              {benchmarkIndustry && (
                <Bar
                  name={messages.primaryBusinessType[benchmarkIndustry]}
                  dataKey="benchmarkIndustry"
                  fill={colors.tertiary}
                />
              )}

              {benchmarkGroup && (
                <Bar
                  name={messages.companyGroup}
                  dataKey="benchmarkGroup"
                  fill={colors.tertiary}
                />
              )}

              <XAxis
                type="number"
                domain={[0, 3]}
                ticks={[0, 0.5, 1, 1.5, 2, 2.5, 3]}
              />
              <YAxis type="category" dataKey="content" width={200} />
              <ReferenceLine x={GRADE_THRESHOLD} stroke={colors.threshold} />
              <Legend
                align="left"
                wrapperStyle={{ left: 204 }}
                formatter={(value) => (
                  <span className="report__legend__label">{value}</span>
                )}
              />
            </ComposedChart>
          </ReportChart>

          {/* Employee Life Cycle steps - this year against the previous one */}
          <ReportChart
            id="page-elc"
            eyebrow={v2Copy.yourResults}
            title={messages.resultsELCSteps}
            number={nextPage()}
          >
            <BarChart
              margin={{ top: 15, right: 5, bottom: 5, left: 5 }}
              width={500}
              height={400}
              data={data.categories}
            >
              <Bar name={year} dataKey="grade" fill={colors.primary}>
                <LabelList position="top" formatter={(value) => `${value}%`} />
              </Bar>
              <XAxis
                type="category"
                dataKey="content"
                width={200}
                interval={0}
                height={140}
                angle="-60"
                textAnchor="end"
              />
              <YAxis type="number" domain={[0, 100]} />

              {hasPrevious && (
                <>
                  <Bar
                    name={previousYear}
                    dataKey="previousGrade"
                    fill={colors.secondary}
                  >
                    <LabelList
                      position="top"
                      formatter={(value) => `${value}%`}
                    />
                  </Bar>
                  <Legend
                    align="left"
                    wrapperStyle={{ left: 64 }}
                    formatter={(value) => (
                      <span className="report__legend__label">{value}</span>
                    )}
                  />
                </>
              )}
            </BarChart>
          </ReportChart>

          {/* Employee Life Cycle steps - against the Employer Partner average */}
          <ReportChart
            id="page-elc-benchmark"
            eyebrow={v2Copy.yourResults}
            title={messages.resultsELCSteps}
            subtitle={messages.benchmarkAnalysis}
            number={nextPage()}
            options={this._renderBenchmarkOptions()}
          >
            <BarChart
              margin={{ top: 15, right: 5, bottom: 5, left: 5 }}
              width={500}
              height={400}
              data={data.categories}
            >
              <Bar
                name={messages.yourOrganization}
                dataKey="grade"
                fill={colors.primary}
              >
                <LabelList position="top" formatter={(value) => `${value}%`} />
              </Bar>
              <Bar
                name={messages.employerPartnerAvg}
                dataKey="others"
                fill={colors.secondary}
              >
                <LabelList position="top" formatter={(value) => `${value}%`} />
              </Bar>

              {benchmarkCountry && (
                <Bar
                  name={messages.countries[benchmarkCountry]}
                  dataKey="benchmarkCountry"
                  fill={colors.tertiary}
                >
                  <LabelList
                    position="top"
                    formatter={(value) => `${value}%`}
                  />
                </Bar>
              )}

              {benchmarkIndustry && (
                <Bar
                  name={messages.primaryBusinessType[benchmarkIndustry]}
                  dataKey="benchmarkIndustry"
                  fill={colors.tertiary}
                >
                  <LabelList
                    position="top"
                    formatter={(value) => `${value}%`}
                  />
                </Bar>
              )}

              {benchmarkGroup && (
                <Bar
                  name={messages.companyGroup}
                  dataKey="benchmarkGroup"
                  fill={colors.tertiary}
                >
                  <LabelList
                    position="top"
                    formatter={(value) => `${value}%`}
                  />
                </Bar>
              )}

              <XAxis
                type="category"
                dataKey="content"
                width={200}
                interval={0}
                height={140}
                angle="-60"
                textAnchor="end"
              />
              <YAxis type="number" domain={[0, 100]} />
              <Legend
                align="left"
                wrapperStyle={{ left: 64 }}
                formatter={(value) => (
                  <span className="report__legend__label">{value}</span>
                )}
              />
            </BarChart>
          </ReportChart>

          {/* Divider: turning insights into action */}
          <V2Page variant="dark" modifier="divider" number={nextPage()}>
            <V2Lockup
              selectio={images.logoSelectioWhite}
              employerPartner={images.logoEpWhite}
            />
            <h2 className="report-v2__title">{v2Copy.turningInsights}</h2>
            <p>{v2Copy.turningInsightsIntro}</p>
          </V2Page>

          {/* One group per Employee Life Cycle step, ordered as in V1: the
              chart slide, then that step's highlights and suggestions, then the
              next step.
              ReportCategoryHightlightsSuggestions renders its own slide plus the
              editing panel beside it, so it is used as-is rather than nested in
              a V2Page - wrapping it would pull that panel inside the slide. */}
          {(data.categories || []).map((category, key) => (
            <React.Fragment key={`category-${key}`}>
              <V2Page
                variant="hex-left"
                background={images.hexLeft}
                modifier="category"
                number={nextPage()}
                id={
                  key === 0 ? "page-highlights" : `page-category-${category.id}`
                }
              >
                <div className="report-v2__split report-v2__split--narrow-left">
                  {/* Same donut size and placement as the highlights /
                      suggestions slides, which use ReportCircleChart at
                      size 120 / stroke 20 inside .report_category__side.
                      `grade` on a category is already a 0-100 percentage - the
                      report action averages the area grades over the 0-3 scale. */}
                  <V2Donut
                    value={category.grade}
                    previousValue={category.previousGrade}
                    size={120}
                    stroke={20}
                    caption={category.content}
                  />
                  <V2BarChart
                    data={this._seriesData(category.areas)}
                    series={categorySeries}
                    ticks={[0, 0.5, 1, 1.5, 2, 2.5, 3]}
                    height={400}
                    categoryWidth={150}
                  />
                </div>
              </V2Page>

              <ReportCategoryHightlightsSuggestions
                number={nextPage()}
                heading={messages.highlights}
                name={`highlights_${category.id}`}
                title={category.content}
                score={category.grade}
                previousScore={category.previousGrade}
                content={content[`highlights_${category.id}`]}
                onChange={this._contentOnChange}
                suggestions={category.highlights}
                editable={editable}
                aiHeading={messages.aiHighlightsHeading}
                aiButtonLabel={messages.getAiHighlights}
                aiItems={aiItems[`highlights_${category.id}`]}
                aiLoading={aiLoading[`highlights_${category.id}`]}
                onGetAi={() => this._getAiItems(category.id, "highlights")}
              />
              <ReportCategoryHightlightsSuggestions
                number={nextPage()}
                heading={messages.suggestions}
                name={`suggestions_${category.id}`}
                title={category.content}
                score={category.grade}
                previousScore={category.previousGrade}
                content={content[`suggestions_${category.id}`]}
                onChange={this._contentOnChange}
                suggestions={category.suggestions}
                editable={editable}
                aiHeading={messages.aiSuggestionsHeading}
                aiButtonLabel={messages.getAiSuggestions}
                aiItems={aiItems[`suggestions_${category.id}`]}
                aiLoading={aiLoading[`suggestions_${category.id}`]}
                onGetAi={() => this._getAiItems(category.id, "suggestions")}
              />
            </React.Fragment>
          ))}

          {/* Your HR practices in numbers - the strengths, then the KPIs worth
              improving. Both are written by the certifier, with the sentences
              from data.highlights offered beside the slide: those at or above
              grade 3 as strengths, those below as things to improve. Same split
              as V1. */}
          <V2EditableHighlights
            id="page-practices-strengths"
            number={nextPage()}
            eyebrow={v2Copy.practices}
            title={v2Copy.practicesStrengths}
            name="highlightsFirst"
            content={content.highlightsFirst}
            onChange={this._contentOnChange}
            suggestions={(data.highlights || []).filter(
              (item) => item.grade >= 3 || item.grade === null,
            )}
            editable={editable}
          />

          {/* A second strengths slide for reports with more to say than fits on
              one page. Always offered while editing; in the printed report it
              is skipped unless the certifier wrote something, so an unused
              second page leaves no blank slide behind - and nextPage() is not
              called for it, so the numbering closes up. Same rule as V1's
              second highlights page and the empty cards / timeline steps. */}
          {(editable || !isEmptyHtml(content.highlightsSecond)) && (
            <V2EditableHighlights
              id="page-practices-strengths2"
              number={nextPage()}
              eyebrow={v2Copy.practices}
              title={v2Copy.practicesStrengths}
              name="highlightsSecond"
              content={content.highlightsSecond}
              onChange={this._contentOnChange}
              suggestions={(data.highlights || []).filter(
                (item) => item.grade >= 3 || item.grade === null,
              )}
              editable={editable}
            />
          )}

          <V2EditableHighlights
            id="page-practices-suggestions"
            number={nextPage()}
            eyebrow={v2Copy.practices}
            title={v2Copy.keepInMind}
            name="suggestions"
            content={content.suggestions}
            onChange={this._contentOnChange}
            suggestions={(data.highlights || []).filter(
              (item) => item.grade < 3 && item.grade !== null,
            )}
            editable={editable}
          />

          {/* Benchmark analysis - absolute numbers */}
          {/* No watermark on the benchmark slides - the hexagons show through
              the light blue track and muddy the bars. */}
          <V2Page
            variant="plain"
            number={nextPage()}
            id="page-practices-benchmark"
          >
            <V2Header
              eyebrow={v2Copy.practices}
              title={messages.benchmarkAnalysis}
            />
            <V2BenchmarkBars
              averageLabel={messages.employerPartnerAvg}
              rows={(data.benchmarks || []).map((item) => ({
                label: item.content,
                value: item.selfValue,
                average: item.otherValue,
                unit: item.unit,
              }))}
            />
          </V2Page>

          {/* Benchmark analysis - percentages, over two slides.
              The split comes from the report payload rather than being sliced
              here, so which metric lands on which slide stays a data decision
              (questionnaireType_question.reportBenchmarkSlide). Type 1 fills
              only the first, and the second is skipped rather than printed
              empty. */}
          {[data.benchmarksSlide2, data.benchmarksSlide3].map((rows, key) =>
            rows && rows.length ? (
              <V2Page key={key} variant="plain" number={nextPage()}>
                <V2Header
                  eyebrow={v2Copy.practices}
                  title={messages.benchmarkAnalysis}
                />
                <V2BarChart
                  data={rows.map((item) => ({
                    name: item.content,
                    current: item.selfValue,
                    benchmark: item.otherValue,
                  }))}
                  series={benchmarkSeries}
                  domain={[0, 100]}
                  ticks={[0, 25, 50, 75, 100]}
                  // Leaves room for the axis and legend inside the 540px page -
                  // at 430 the legend sat on the bottom edge.
                  height={400}
                  showValues
                  // A zero here means the figure was not recorded rather than a
                  // measured zero, so it reads as n/a - same rule as the
                  // absolute-numbers slide. 0.5111 / 0.5112 are the sentinels
                  // the report query uses for a missing value.
                  valueFormatter={(value) =>
                    !value || value === 0.5111 || value === 0.5112
                      ? "n/a"
                      : `${Math.round(value)}%`
                  }
                  // The metric names are full sentences, so they get roughly
                  // the same share of the width as V1 gives them (300 of 700).
                  categoryWidth={340}
                />
              </V2Page>
            ) : null,
          )}

          {/* Development activities - separate editable lists for employees
              and managers. Cards left empty are hidden in the printed report. */}
          <V2Page
            modifier="development"
            number={nextPage()}
            id="page-educations"
          >
            <V2Header title={v2Copy.developmentActivities} />
            <p>{v2Copy.developmentActivitiesIntro}</p>

            {/* The two rows are centred in the space left below the intro. */}
            <div className="report-v2__development-rows">
              <p className="report-v2__card-label">{v2Copy.forEmployees}</p>
              <V2EditableCards
                name="developmentEmployees"
                className="report-v2__editable--row"
                slots={5}
                items={this._cardList("developmentEmployees")}
                suggestions={data.educations}
                options={v2Copy.developmentOptions}
                editable={editable}
                onChange={this._contentOnChange}
                addLabel={v2Copy.addCard}
              />

              <p className="report-v2__card-label">{v2Copy.forManagers}</p>
              <V2EditableCards
                name="developmentManagers"
                className="report-v2__editable--row"
                slots={5}
                items={this._cardList("developmentManagers")}
                suggestions={data.educations}
                options={v2Copy.developmentOptions}
                editable={editable}
                onChange={this._contentOnChange}
                addLabel={v2Copy.addCard}
              />
            </div>
          </V2Page>

          {/* SELECTIO services */}
          <V2Page
            variant="dark"
            modifier="services"
            background={images.bgNoHex}
            number={nextPage()}
          >
            <div className="report-v2__split">
              <div className="report-v2__services-intro">
                <h2 className="report-v2__title">{v2Copy.servicesTitle}</h2>
                <p>{v2Copy.servicesIntro}</p>
                <p>{v2Copy.servicesOutro}</p>
                {/* The slide is about SELECTIO's services, so the call to
                    action goes to selectio.hr rather than employerpartner.eu -
                    which is where the outro's website panel still points. The
                    URL is per language, so the English report lands on /en. */}
                <V2Chip href={v2Copy.servicesUrl}>{v2Copy.servicesCta}</V2Chip>
              </div>
              {/* The four hexagons interlock at positions that do not follow
                  any regular grid, so each one is placed absolutely against the
                  cluster box. Nudge a hexagon by editing its left/top in
                  `servicesHexPositions` below - order matches v2Copy.services
                  (Hiring, Assessment, HR Consulting, Educations). */}
              <div className="report-v2__hex-cards">
                {v2Copy.services.map((service, key) => (
                  <div
                    className="report-v2__hex-cards-slot"
                    key={key}
                    style={servicesHexPositions[key]}
                  >
                    <V2HexCard title={service.title} outline={images.hexWhite}>
                      {service.text}
                    </V2HexCard>
                  </div>
                ))}
              </div>
            </div>
          </V2Page>

          {/* Official confirmation */}
          <V2Page
            modifier="confirmation"
            number={nextPage()}
            id="page-confirmation"
          >
            <div className="report-v2__split">
              <div className="report-v2__body">
                <V2Header title={v2Copy.officialConfirmation} />
                {v2Copy.officialConfirmationText.map((paragraph, key) => (
                  <p key={key}>{paragraph}</p>
                ))}
              </div>
              {/* `certifiers` is a single row with first/second/third prefixed
                  columns rather than a list - see the report action in
                  cep-frontend.js. Entries without a name are skipped. */}
              <div className="report-v2__certifiers">
                {["first", "second", "third"]
                  .map((prefix) => ({
                    name: (data.certifiers || {})[prefix],
                    company: (data.certifiers || {})[`${prefix}Subtitle`],
                    role: (data.certifiers || {})[`${prefix}Title`],
                  }))
                  .filter((certifier) => certifier.name)
                  .map((certifier, key) => (
                    <V2Card key={key} title={certifier.name}>
                      <span>{certifier.company}</span>
                      <span>{certifier.role}</span>
                    </V2Card>
                  ))}
              </div>
            </div>
          </V2Page>

          {/* Employer brand visibility */}
          <V2Page
            variant="plain"
            modifier="visibility"
            number={nextPage()}
            id="page-whats-next"
          >
            <V2Header title={v2Copy.visibility} />
            {/* The date the certification runs to is agreed per client, so it
                is edited on the report rather than derived from the year. */}
            <V2EditableSentence
              template={v2Copy.visibilityIntro}
              name="visibilityDate"
              value={content.visibilityDate || ""}
              placeholder={v2Copy.visibilityDatePlaceholder}
              editable={editable}
              onChange={this._contentOnChange}
            />
            <p>{v2Copy.visibilityOutro}</p>
            <V2EditableTimeline
              name="visibilitySteps"
              steps={this._cardList("visibilitySteps", v2Copy.visibilitySteps)}
              editable={editable}
              onChange={this._contentOnChange}
              addLabel={v2Copy.addStep}
            />
          </V2Page>

          {/* Timeline */}
          <V2Page
            variant="plain"
            modifier="timeline"
            number={nextPage()}
            id="page-timeline"
          >
            <V2Header title={v2Copy.timeline} />
            <p>{v2Copy.timelineIntro}</p>
            <V2EditableTimeline
              variant="cards"
              name="timelineSteps"
              steps={this._cardList("timelineSteps", [
                {
                  date: `${v2Copy.months.july}, ${year}`,
                  title: v2Copy.timelineSteps[0],
                },
                {
                  date: `${v2Copy.months.december}, ${year}`,
                  title: v2Copy.timelineSteps[1],
                },
                {
                  date: `${v2Copy.months.marchApril} ${year + 1}`,
                  title: v2Copy.timelineSteps[2],
                },
                {
                  date: `${v2Copy.months.june}, ${year + 1}`,
                  title: v2Copy.timelineSteps[3],
                },
              ])}
              editable={editable}
              onChange={this._contentOnChange}
              addLabel={v2Copy.addStep}
            />
          </V2Page>

          {/* Outro - the closing slide is not numbered, like the cover. */}
          <V2Page variant="dark" modifier="outro">
            <div className="report-v2__outro-body">
              <h2
                className="report-v2__outro-title"
                dangerouslySetInnerHTML={{ __html: v2Copy.outroTitle }}
              />
              <p className="report-v2__outro-contact">
                {contact.company}
                <br />
                {contact.address}
                <br />
                {contact.email}
              </p>
            </div>
            <div className="report-v2__footer-logos">
              <a
                className="report-v2__outro-web"
                href="https://employerpartner.eu/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {v2Copy.outroWebsite}
              </a>
              <img
                className="report-v2__lockup-selectio"
                src={images.logoSelectioWhite}
                alt="SELECTIO"
              />
              <img
                className="report-v2__lockup-ep"
                src={images.logoEpWhite}
                alt="Employer Partner"
              />
              <span className="report-v2__footer-spacer" />
              {/* Partner panel - sits on its own tinted background. */}
              <span className="report-v2__partners">
                <span className="report-v2__partners-label">
                  {v2Copy.partnerLabel}
                </span>
                <img src={images.partnerAlmaCareer} alt="Alma Career" />
                <img
                  className="report-v2__partner-mojposao"
                  src={images.partnerMojPosao}
                  alt="MojPosao"
                />
              </span>
            </div>
          </V2Page>
        </div>
      </div>
    );
  }
}
